import React, { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '../../../utils/cn';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  dragSensitivity?: number;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  label,
  min,
  max,
  step = 1,
  disabled = false,
  className,
  dragSensitivity = 0.5,
}) => {
  const [localValue, setLocalValue] = useState(String(value));
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragStartRef = useRef({ x: 0, value: 0 });

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(String(Math.round(value * 100) / 100));
    }
  }, [value, isEditing]);

  const clampValue = useCallback(
    (val: number): number => {
      let result = val;
      if (min !== undefined) result = Math.max(min, result);
      if (max !== undefined) result = Math.min(max, result);
      return result;
    },
    [min, max]
  );

  const roundToStep = useCallback(
    (val: number): number => {
      if (step === 0) return val;
      return Math.round(val / step) * step;
    },
    [step]
  );

  /** Round to at most 2 decimal places */
  const roundDisplay = (val: number): number =>
    Math.round(val * 100) / 100;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const parsed = parseFloat(localValue);
    if (!isNaN(parsed)) {
      const clamped = roundDisplay(clampValue(parsed));
      onChange(clamped);
      setLocalValue(String(clamped));
    } else {
      setLocalValue(String(value));
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    // Place cursor at the end instead of selecting all
    const input = inputRef.current;
    if (input) {
      const len = input.value.length;
      requestAnimationFrame(() => input.setSelectionRange(len, len));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setLocalValue(String(value));
      setIsEditing(false);
      inputRef.current?.blur();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newValue = roundDisplay(clampValue(value + step));
      onChange(newValue);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newValue = roundDisplay(clampValue(value - step));
      onChange(newValue);
    }
  };

  const handleLabelMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();

      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, value };

      const handleMouseMove = (e: MouseEvent) => {
        const delta = e.clientX - dragStartRef.current.x;
        const scaledDelta = delta * dragSensitivity * step;
        const newValue = roundDisplay(roundToStep(clampValue(dragStartRef.current.value + scaledDelta)));
        onChange(newValue);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },
    [disabled, value, step, dragSensitivity, clampValue, roundToStep, onChange]
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {label && (
        <label
          className={cn(
            'text-xs text-zinc-400 select-none min-w-[16px] cursor-ew-resize',
            isDragging && 'text-blue-400',
            disabled && 'cursor-default opacity-50'
          )}
          onMouseDown={handleLabelMouseDown}
        >
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded',
          'text-zinc-100 focus:outline-none focus:border-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
        )}
      />
    </div>
  );
};
